import React, { useState } from 'react';
import { useSubscriptionPlanList } from '@/hooks/queries/subscription-plan/useList';
import { useSubscriptionPlanCreate, useSubscriptionPlanUpdate, useSubscriptionPlanDelete } from '@/hooks/mutations/subscription-plan';
import { PremiumPlan as SubscriptionPlan, PremiumPlanInputType } from '@/types/premium-plan';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/fields';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, CreditCard } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Pagination } from '@/components/Pagination';

const SubscriptionPlansPage = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);

  const [formData, setFormData] = useState<PremiumPlanInputType>({
    price: 299000,
    duration: 1,
    isActive: true,
    translations: [
      { locale: 'uz', title: '', description: '' },
      { locale: 'ru', title: '', description: '' },
      { locale: 'en', title: '', description: '' },
    ],
  });

  const { data: plansData, isLoading, refetch } = useSubscriptionPlanList({ pageNumber: page, pageSize: 20 });
  const { mutate: createPlan, isPending: isCreating } = useSubscriptionPlanCreate();
  const { mutate: updatePlan, isPending: isUpdating } = useSubscriptionPlanUpdate();
  const { mutate: deletePlan } = useSubscriptionPlanDelete();

  const handleOpenCreate = () => {
    setSelectedPlan(null);
    setFormData({
      price: 299000,
      duration: 1,
      isActive: true,
      translations: [
        { locale: 'uz', title: '', description: '' },
        { locale: 'ru', title: '', description: '' },
        { locale: 'en', title: '', description: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setFormData({
      price: plan.price,
      duration: plan.duration,
      isActive: plan.isActive,
      translations: [
        { locale: 'uz', title: plan.title || '', description: plan.description || '' },
        { locale: 'ru', title: '', description: '' },
        { locale: 'en', title: '', description: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleTranslationChange = (locale: 'uz' | 'ru' | 'en', field: 'title' | 'description', value: string) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, [field]: value } : t)),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedPlan) {
      updatePlan(
        { id: selectedPlan.id, data: formData },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Obuna rejasi yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Reja yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createPlan(formData, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi obuna rejasi yaratildi' });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Reja yaratilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedPlan) return;
    deletePlan(selectedPlan.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Obuna rejasi o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedPlan(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Reja o'chirilmadi", variant: 'destructive' }),
    });
  };

  const plansList: SubscriptionPlan[] = plansData?.data?.data || plansData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Obuna Rejalari</h1>
          <p className="text-sm text-slate-500 mt-1">Platformadagi subscription planlar va tariflar</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi reja
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : plansList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <CreditCard className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha obuna rejalari mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Reja Nomi</th>
                  <th className="px-4 py-3.5">Narxi</th>
                  <th className="px-4 py-3.5">Davomiyligi</th>
                  <th className="px-4 py-3.5">Holati</th>
                  <th className="px-4 py-3.5">Tavsif</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {plansList.map((plan) => (
                  <tr key={plan.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-slate-900">{plan.title || '—'}</td>
                    <td className="px-4 py-3 text-slate-900 font-medium">{plan.price?.toLocaleString()} so'm</td>
                    <td className="px-4 py-3 text-slate-600">{plan.duration} oy</td>
                    <td className="px-4 py-3">
                      <Badge variant={plan.isActive ? 'default' : 'secondary'} className={plan.isActive ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : ''}>
                        {plan.isActive ? 'Faol' : 'Nofaol'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 max-w-xs truncate text-slate-500">{plan.description || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(plan)} className="size-8 text-slate-600 hover:text-primary">
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedPlan(plan);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={plansData?.meta?.pagination ?? plansData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedPlan ? 'Obuna rejasini tahrirlash' : 'Yangi obuna rejasi yaratish'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Narxi (so'm)</Label>
                <Input
                  type="number"
                  placeholder="299000"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                  className="rounded-xl"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Davomiyligi (oy)</Label>
                <Input
                  type="number"
                  placeholder="1"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: Number(e.target.value) })}
                  className="rounded-xl"
                  required
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div>
                <Label className="text-sm font-semibold text-slate-800">Faol holati</Label>
                <p className="text-xs text-slate-500">Ushbu reja foydalanuvchilarga ko'rinsinmi</p>
              </div>
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
            </div>

            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli ma'lumotlar</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha (UZ)</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский (RU)</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English (EN)</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = formData.translations.find((t) => t.locale === locale) || { title: '', description: '' };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Reja nomi ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.title || ''}
                          onChange={(e) => handleTranslationChange(locale, 'title', e.target.value)}
                          placeholder="Oylik Premium"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Tavsif ({locale.toUpperCase()})</Label>
                        <MarkdownEditor
                          value={translation.description || ''}
                          onChange={(v) => handleTranslationChange(locale, 'description', v)}
                          placeholder="Barcha kurslarga cheksiz kirish..."
                          minHeight={80}
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Obuna rejasini o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu reja o'chirilgach, kesh yangilanadi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default SubscriptionPlansPage;
